import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Loader2, Lock, FileDown, Timer, Trophy, Gift, Banknote } from "lucide-react";
import { toast } from "sonner";
import { CAMPAIGN, getNextDraw, DRAW_SCHEDULE } from "@/config/campaign";
import { submitEntry, trackEvent, trackFormError, BONUS_URL } from "@/lib/api";
import { trackLead } from "@/lib/meta";
import { getPhase } from "@/lib/phase";
import { EASE } from "@/components/Reveal";

const inputBase =
  "h-14 w-full rounded-[14px] border bg-white px-4 text-base text-ink placeholder:text-mutedlight/50 transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-1";
const inputOk = `${inputBase} border-line focus:border-gold focus:ring-gold`;
const inputErr = `${inputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

const formatPhone = (value) => {
  let digits = value.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) digits = "+" + digits.slice(1).replace(/\D/g, "");
  else digits = digits.replace(/\D/g, "");
  if (digits.startsWith("0041")) digits = "+41" + digits.slice(4);
  if (digits.startsWith("41") && !digits.startsWith("+")) digits = "+41" + digits.slice(2);
  if (digits.startsWith("+41")) {
    const rest = digits.slice(3).replace(/\D/g, "").slice(0, 9);
    const parts = [rest.slice(0, 2), rest.slice(2, 5), rest.slice(5, 7), rest.slice(7, 9)].filter(Boolean);
    return "+41" + (rest ? " " + parts.join(" ") : "");
  }
  if (digits.startsWith("0")) {
    const parts = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 8), digits.slice(8, 10)].filter(Boolean);
    return parts.join(" ");
  }
  return digits;
};

const Field = ({ label, error, hint, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-2 block text-sm font-medium text-ink">
      {label}
    </label>
    {children}
    {error ? (
      <p className="mt-2 text-sm text-red-600" role="alert" data-testid={`${htmlFor}-error`}>{error}</p>
    ) : hint ? (
      <div className="mt-2 text-xs text-mutedlight">{hint}</div>
    ) : null}
  </div>
);

const initialForm = {
  first_name: "", last_name: "", email: "", phone: "",
  terms_accepted: false, chickenwinner_newsletter_consent: false, website: "",
};

const validate = (f) => {
  const e = {};
  if (!f.first_name.trim()) e.first_name = "Bitte gib deinen Vornamen ein.";
  if (!f.last_name.trim()) e.last_name = "Bitte gib deinen Nachnamen ein.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = "Bitte gib eine gültige E-Mail-Adresse ein.";
  const digits = f.phone.replace(/\D/g, "");
  if (digits.length < 9 || digits.length > 13) e.phone = "Bitte gib eine gültige Schweizer Handynummer ein.";
  if (!f.terms_accepted) e.terms_accepted = "Bitte bestätige die Teilnahmebedingungen und die Datenschutzerklärung.";
  return e;
};

const errorCodesFor = (errs) => {
  const codes = [];
  if (errs.email) codes.push("invalid_email");
  if (errs.phone) codes.push("invalid_phone");
  if (errs.first_name || errs.last_name || errs.terms_accepted) codes.push("required_field_missing");
  return codes;
};

const PrizeRow = ({ icon: Icon, count, label }) => (
  <div className="flex items-center gap-3 rounded-[14px] border border-line bg-offwhite px-4 py-3">
    <Icon className="h-5 w-5 shrink-0 text-gold-dark" strokeWidth={1.75} />
    <span className="text-sm font-semibold text-ink">{count}× {label}</span>
  </div>
);

const SuccessCard = () => {
  const nxt = getNextDraw();
  return (
    <motion.div
      key="success"
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: EASE }}
      className="py-4 text-center"
      data-testid="success-card"
    >
      <h3 className="font-display text-4xl font-extrabold tracking-tight text-ink">🎉 Du bist dabei!</h3>
      <p className="mt-3 text-mutedlight">Deine Teilnahme wurde erfolgreich registriert.</p>
      <p className="mt-2 text-sm font-medium text-ink">Du bist jetzt automatisch bei allen kommenden Ziehungen dabei.</p>

      <div className="mt-6 grid grid-cols-1 gap-2.5 text-left sm:grid-cols-3">
        <PrizeRow icon={Trophy} count={1} label="7 Jahre Steuererklärung gratis" />
        <PrizeRow icon={Gift} count={20} label="Steuererklärung 2027 gratis" />
        <PrizeRow icon={Banknote} count={5} label="CHF 100 Cash" />
      </div>

      <div className="mt-8 rounded-[18px] border border-gold/40 bg-gold-dim p-6" data-testid="success-bonus-block">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-dark">🎁 Dein Sofortbonus ist bereit</p>
        <p className="mt-2 text-sm text-coal">Wir haben dir dein Steuer-Sparpaket 2027 per E-Mail gesendet.</p>
        <a
          data-testid="bonus-download-button"
          href={BONUS_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent("bonus_download_click", "success_card");
            trackEvent("bonus_download_success", "success_card");
          }}
          className="mt-4 inline-flex h-12 items-center justify-center gap-2 rounded-[14px] bg-gold px-6 text-sm font-bold text-ink transition-[transform,background-color] duration-200 hover:bg-gold-dark active:scale-95"
        >
          <FileDown className="h-4 w-4" strokeWidth={2} /> Steuer-Sparpaket jetzt öffnen
        </a>
      </div>

      {nxt && (
        <p className="mt-6 text-sm text-mutedlight" data-testid="success-next-draw">
          Nächste Ziehung: <span className="font-semibold text-ink">{nxt.label}</span>
        </p>
      )}
    </motion.div>
  );
};

export const GiveawayForm = ({ utm, onSuccess }) => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const startedRef = useRef(false);
  const leadSentRef = useRef(false);
  const cardRef = useRef(null);
  const phase = getPhase();

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        trackEvent("hero_form_view");
        obs.disconnect();
      }
    }, { threshold: 0.25 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const set = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: key === "phone" ? formatPhone(value) : value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const markStarted = () => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("form_start");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    trackEvent("submit_attempt");
    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      trackEvent("form_error", "validation");
      errorCodesFor(errs).forEach(trackFormError);
      const first = document.querySelector("[data-invalid='true']");
      first?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      await submitEntry({
        ...form,
        consent_version: CAMPAIGN.consentVersion,
        referrer: document.referrer || null,
        ...utm,
      });
      trackEvent("registration_success");
      if (!leadSentRef.current) {
        leadSentRef.current = true;
        trackLead(); // Meta-Standard-Event: nur einmal, nur nach erfolgreicher Speicherung
      }
      setSubmitted(true);
      onSuccess?.();
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        confetti({ particleCount: 110, spread: 75, origin: { y: 0.6 }, colors: ["#C49A42", "#FFFFFF", "#111111"] });
      }
    } catch (err) {
      trackEvent("form_error", "submit");
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 409) {
        const reason = typeof detail === "object" ? detail?.reason : "duplicate_email";
        trackFormError(reason === "duplicate_phone" ? "duplicate_phone" : "duplicate_email");
        toast.info(DUPLICATE_TEXT);
      } else if (status === 410) {
        toast.info("Der Wettbewerb ist leider bereits beendet.");
      } else if (status === 423) {
        toast.info(`Der Wettbewerb startet am ${CAMPAIGN.launchLabel}.`);
      } else if (status === 429) {
        trackFormError("backend_error");
        toast.error("Zu viele Anfragen. Bitte versuche es später erneut.");
      } else {
        trackFormError(status === 422 ? "required_field_missing" : "backend_error");
        toast.error("Das hat leider nicht funktioniert. Bitte versuche es nochmals.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div ref={cardRef} className="w-full rounded-[20px] border border-line bg-white p-6 shadow-[0_20px_60px_rgba(17,17,17,0.08)] md:p-8" data-testid="giveaway-form-card">
      <AnimatePresence mode="wait">
        {submitted ? (
          <SuccessCard />
        ) : phase === "closed" ? (
          <div className="py-8 text-center" data-testid="form-closed-state">
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-ink md:text-4xl">Teilnahme beendet.</h2>
            <p className="mt-4 text-mutedlight">
              Der Wettbewerb endete am {CAMPAIGN.deadlineLabel}. Die Gewinner werden nach der Ziehung telefonisch und per E-Mail kontaktiert.
            </p>
          </div>
        ) : (
          <motion.div key="form" exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.3 }}>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-ink">Jetzt kostenlos teilnehmen</h2>
            <p className="mt-1.5 text-sm text-mutedlight">In weniger als einer Minute bist du dabei.</p>
            {phase === "prelaunch" && (
              <div className="mt-4 flex items-center gap-3 rounded-[14px] border border-gold/40 bg-gold-dim p-4" data-testid="form-prelaunch-notice">
                <Timer className="h-5 w-5 shrink-0 text-gold-dark" strokeWidth={2} />
                <p className="text-sm font-medium text-ink">
                  Der Wettbewerb startet am {CAMPAIGN.launchLabel}. Eine Teilnahme ist ab dann möglich.
                </p>
              </div>
            )}
            <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4" data-testid="giveaway-form">
              <input
                type="text" name="website" value={form.website} onChange={set("website")}
                tabIndex={-1} autoComplete="off" aria-hidden="true"
                className="absolute left-[-9999px] h-0 w-0 opacity-0"
              />
              <fieldset disabled={phase === "prelaunch"} className="space-y-4 disabled:opacity-60">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Vorname" error={errors.first_name} htmlFor="first_name">
                    <input id="first_name" data-testid="first-name-input" data-invalid={!!errors.first_name} type="text" autoComplete="given-name" placeholder="Maria" value={form.first_name} onChange={set("first_name")} onFocus={markStarted} className={errors.first_name ? inputErr : inputOk} />
                  </Field>
                  <Field label="Nachname" error={errors.last_name} htmlFor="last_name">
                    <input id="last_name" data-testid="last-name-input" data-invalid={!!errors.last_name} type="text" autoComplete="family-name" placeholder="Muster" value={form.last_name} onChange={set("last_name")} className={errors.last_name ? inputErr : inputOk} />
                  </Field>
                </div>
                <Field label="E-Mail-Adresse" error={errors.email} hint="Hierhin senden wir dir deinen kostenlosen Sofortbonus." htmlFor="email">
                  <input id="email" data-testid="email-input" data-invalid={!!errors.email} type="email" autoComplete="email" inputMode="email" placeholder="maria@beispiel.ch" value={form.email} onChange={set("email")} onFocus={() => { markStarted(); trackEvent("email_field_focus"); }} className={errors.email ? inputErr : inputOk} />
                </Field>
                <Field
                  label="Handynummer"
                  error={errors.phone}
                  htmlFor="phone"
                >
                  <input id="phone" data-testid="phone-input" data-invalid={!!errors.phone} type="tel" autoComplete="tel" inputMode="tel" placeholder="+41 79 123 45 67" value={form.phone} onChange={set("phone")} onFocus={() => { markStarted(); trackEvent("phone_field_focus"); }} className={errors.phone ? inputErr : inputOk} />
                </Field>
                <div className="space-y-3 pt-1">
                  <div>
                    <label className="flex cursor-pointer items-start gap-3" data-testid="terms-checkbox-label">
                      <input type="checkbox" data-testid="terms-checkbox" data-invalid={!!errors.terms_accepted} checked={form.terms_accepted} onChange={set("terms_accepted")} className="mt-1 h-5 w-5 shrink-0 cursor-pointer accent-[#C49A42]" />
                      <span className="text-sm leading-relaxed text-ink">
                        Ich bestätige, dass ich mindestens 18 Jahre alt bin und akzeptiere die{" "}
                        <Link to="/teilnahmebedingungen" target="_blank" className="font-medium text-gold-dark underline underline-offset-2">Teilnahmebedingungen</Link>{" "}
                        sowie die{" "}
                        <Link to="/datenschutz" target="_blank" className="font-medium text-gold-dark underline underline-offset-2">Datenschutzerklärung</Link>.
                      </span>
                    </label>
                    {errors.terms_accepted && <p className="mt-2 text-sm text-red-600" role="alert" data-testid="terms-error">{errors.terms_accepted}</p>}
                  </div>
                  <label className="flex cursor-pointer items-start gap-3 rounded-[14px] border border-line bg-offwhite p-4" data-testid="newsletter-checkbox-label">
                    <input type="checkbox" data-testid="newsletter-checkbox" checked={form.chickenwinner_newsletter_consent} onChange={set("chickenwinner_newsletter_consent")} className="mt-1 h-5 w-5 shrink-0 cursor-pointer accent-[#C49A42]" />
                    <span className="text-xs leading-relaxed text-mutedlight">
                      Ich möchte über zukünftige ChickenWinner Wettbewerbe und neue Gewinne per E-Mail informiert werden. <span className="font-medium">(freiwillig)</span>
                    </span>
                  </label>
                </div>
              </fieldset>
              <button
                type="submit"
                data-testid="form-submit-button"
                disabled={submitting || phase === "prelaunch"}
                className="flex h-14 w-full items-center justify-center gap-2 rounded-[14px] bg-gold font-display text-lg font-extrabold tracking-tight text-ink transition-[transform,background-color,opacity] duration-200 hover:bg-gold-dark active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting
                  ? (<><Loader2 className="h-5 w-5 animate-spin" /> Teilnahme wird gespeichert …</>)
                  : phase === "prelaunch" ? `Start am ${CAMPAIGN.launchLabel.replace(" 2026", "")}` : "Jetzt kostenlos teilnehmen"}
              </button>
              <p className="text-center text-xs font-medium text-mutedlight" data-testid="form-duration-note">
                Dauert weniger als 30 Sekunden.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 border-t border-line pt-4 text-xs text-mutedlight sm:grid-cols-3" data-testid="form-trust-list">
                {["100 % kostenlose Teilnahme", "Keine Kaufpflicht", "26 echte Gewinner", "Schweizer Wettbewerb", "Transparente Ziehung", "Sichere Datenübertragung"].map((t) => (
                  <span key={t} className="flex items-center gap-1.5">
                    <Lock className="h-3 w-3 shrink-0 text-gold-dark" strokeWidth={2} /> {t}
                  </span>
                ))}
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DUPLICATE_TEXT = "Du bist bereits registriert.";
